import {
	DailyUniqueIssuesData,
	IssuesCount
} from 'src/apps/dashboard/services/queries/dashboard/helpers/transformResults';

import { CHART_CONSTANTS } from '../constants';
import { ChartDataPoint, ChartIssuesInput } from '../types';

type ChartPattern = {
	id: string;
	label: string;
	color: string;
};

type TransformResult = {
	chartData: ChartDataPoint[];
	patterns: ChartPattern[];
	maxValue: number;
};

const CHART_PATTERNS: ChartPattern[] = [
	{
		id: 'critical',
		label: 'Critical',
		color: CHART_CONSTANTS.COLORS.CRITICAL
	},
	{
		id: 'serious',
		label: 'Serious',
		color: CHART_CONSTANTS.COLORS.SERIOUS
	}
];

const formatDateForChart = (date: Date): string => {
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: '2-digit',
		year: 'numeric'
	});
};

/**
 * Type guard to check if data is IssuesCount[] (has 'day' property)
 */
const isIssuesCountArray = (data: ChartIssuesInput): data is IssuesCount[] => {
	return data.length > 0 && 'day' in data[0];
};

/**
 * Generic transformer that handles both IssuesCount[] and DailyUniqueIssuesData[]
 * Automatically detects the input type and transforms accordingly
 */
export const prepareDataForStackedChart = (data: ChartIssuesInput): TransformResult => {
	if (!data?.length) {
		return { chartData: [], patterns: [], maxValue: 0 };
	}

	if (isIssuesCountArray(data)) {
		return prepareDataForCriticalIssuesStackedChart(data);
	}

	return prepareDataForUniqueIssuesStackedChart(data);
};

const prepareDataForCriticalIssuesStackedChart = (data: IssuesCount[]): TransformResult => {
	if (!data?.length) {
		return { chartData: [], patterns: [], maxValue: 0 };
	}

	const sortedData = [...data].sort((a, b) => a.day.getTime() - b.day.getTime());
	const chartData: ChartDataPoint[] = sortedData.map((item) => ({
		date: formatDateForChart(item.day),
		critical: item.critical,
		serious: item.serious,
		total: item.critical + item.serious,
		allIssuesTotal: item.total
	}));

	const allValues = sortedData.flatMap((item) => [
		item.critical || 0,
		item.serious || 0,
		(item.critical || 0) + (item.serious || 0)
	]);
	const maxValue = Math.max(...allValues);

	return {
		chartData,
		patterns: CHART_PATTERNS,
		maxValue
	};
};

const prepareDataForUniqueIssuesStackedChart = (data: DailyUniqueIssuesData[]): TransformResult => {
	if (!data?.length) {
		return { chartData: [], patterns: [], maxValue: 0 };
	}

	const sortedData = [...data].sort(
		(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
	);
	const chartData: ChartDataPoint[] = sortedData.map((item) => {
		const date = new Date(item.date);
		return {
			date: formatDateForChart(date),
			critical: item.critical,
			serious: item.serious,
			total: item.critical + item.serious,
			allIssuesTotal: item.total
		};
	});

	const allValues = sortedData.flatMap((item) => [
		item.critical || 0,
		item.serious || 0,
		(item.critical || 0) + (item.serious || 0)
	]);
	const maxValue = Math.max(...allValues);

	return {
		chartData,
		patterns: CHART_PATTERNS,
		maxValue
	};
};
